import { LuIcon, Menu, type MenuClickTriggerProps, type MenuRootProps, type MenuVariantProps } from "@overmap-ai/blocks"
import { useTextFilter } from "@overmap-ai/hooks"
import { Search } from "lucide-react"
import { memo, type ReactElement } from "react"

import { PROCEDURE_STEP_COMPLETION_STATUSES } from "../../constants"
import { ProcedureStepCompletionStatus } from "../../enums/procedures"
import { procedureStepCompletionStatusFilterCallback } from "../../filtering/utils"
import {
	ProcedureStepCompletionStatusSelectGroup,
	type ProcedureStepCompletionStatusSelectGroupProps,
} from "../MenuSelectGroups/ProcedureStepCompletionStatusSelectGroup"

type ProcedureStepCompletionStatusSelectProps = Omit<MenuRootProps, "children"> &
	Omit<MenuClickTriggerProps, "children"> &
	MenuVariantProps &
	ProcedureStepCompletionStatusSelectGroupProps & {
		children: ReactElement
	}

export const ProcedureStepCompletionStatusSelect = memo<ProcedureStepCompletionStatusSelectProps>((props) => {
	const {
		// Trigger props
		children,
		disabled = false,
		// MenuContent props
		size,
		radius,
		variant,
		accentColor,
		// Status props
		statuses = PROCEDURE_STEP_COMPLETION_STATUSES,
		required,
		defaultValue,
		value,
		onValueChange,
		// Rest props
		...rest
	} = props

	const [filteredStatuses, filterValue, setFilterValue] = useTextFilter<ProcedureStepCompletionStatus>(
		statuses,
		procedureStepCompletionStatusFilterCallback,
	)

	return (
		<Menu.Root {...rest}>
			<Menu.ClickTrigger disabled={disabled}>{children}</Menu.ClickTrigger>
			<Menu.Content size={size} radius={radius} variant={variant} accentColor={accentColor}>
				<Menu.InputRoot>
					<Menu.InputSlot>
						<LuIcon icon={Search} />
					</Menu.InputSlot>
					<Menu.InputField placeholder="Search" value={filterValue} onValueChange={setFilterValue} />
				</Menu.InputRoot>
				{filteredStatuses.length > 0 && <Menu.Separator />}
				<Menu.Scroll>
					{required ? (
						<ProcedureStepCompletionStatusSelectGroup
							statuses={filteredStatuses}
							required={required}
							value={value}
							defaultValue={defaultValue}
							onValueChange={onValueChange}
						/>
					) : (
						<ProcedureStepCompletionStatusSelectGroup
							statuses={filteredStatuses}
							required={required}
							value={value}
							defaultValue={defaultValue}
							onValueChange={onValueChange}
						/>
					)}
				</Menu.Scroll>
			</Menu.Content>
		</Menu.Root>
	)
})
ProcedureStepCompletionStatusSelect.displayName = "ProcedureStepCompletionStatusSelect"
